import React, { useEffect, useRef, useState, useCallback } from 'react';

import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import InnerHeader from '../../components/Auth/InnerHeader';
import Role from '../../components/Table/Role';
import Loader from '../../components/Loader';

import classes from './styles.module.scss';
import UserService from '../../services/UserService';
import AdminService from '../../services/AdminService';
import {
  saveAuthData,
  getAuthData,
  clearAuthData,
  getTokenData,
} from '../../helpers/authStorage';
import { useUserStore } from '../../store';

export default function ContextSwitchPage() {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isSelecting, setIsSelecting] = useState(false);
  const isMountedRef = useRef(true);

  const {
    setScreeningAvailable,
    setIsAgency,
    setUserRoles,
    setIsClient,
    setIsInterviewer,
    setCompanyId,
  } = useUserStore();

  const checkScreeningAvailability = useCallback(async () => {
    try {
      const response = await AdminService.getTenantAssessmentProviders({
        term: '',
        pageSize: 9999,
      });
      const hasProviders =
        response?.data &&
        Array.isArray(response.data) &&
        response.data.length > 0;
      setScreeningAvailable(hasProviders);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to check screening availability:', error);
      setScreeningAvailable(false);
    }
  }, [setScreeningAvailable]);

  const setTenantTypeFromToken = useCallback(() => {
    try {
      const tokenData = getTokenData();
      const tenantType = tokenData?.tenantType;
      const companyId = tokenData?.companyId;
      const rawRoles =
        tokenData.role ?? tokenData.roles ?? tokenData.Role ?? tokenData.Roles;
      let rolesList = [];
      if (Array.isArray(rawRoles)) {
        rolesList = rawRoles;
      } else if (typeof rawRoles === 'string') {
        rolesList = rawRoles.split(/[ ,]+/).filter(Boolean);
      }
      setUserRoles(rolesList);

      const hasClientRole = rolesList.some(
        (r) => String(r).toLowerCase() === 'client'
      );

      const hasInterviewerRole = rolesList.some(
        (r) => String(r).toLowerCase() === 'interviewer'
      );

      setIsClient(hasClientRole);
      setIsInterviewer(hasInterviewerRole);
      if (companyId) {
        setCompanyId(companyId);
      } else {
        setCompanyId(null);
      }
      const isAgencyTenant =
        tenantType === 'Agency' && !hasClientRole && !hasInterviewerRole;
      setIsAgency(isAgencyTenant);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to extract tenant type from token:', error);
      setIsAgency(false);
    }
  }, [setIsAgency, setUserRoles, setIsClient, setIsInterviewer, setCompanyId]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const logout = () => {
    clearAuthData();
    queryClient.invalidateQueries();
    navigate('/', { replace: true });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['contexts'],
    queryFn: UserService.getContexts,
  });

  return (
    <div className={classes.ContextSwitchPage}>
      <Helmet>
        <title>{t('pages.ContextSwitchPage.pageTitle')}</title>
      </Helmet>
      <div className={classes.container}>
        <InnerHeader
          label={t('common.signOut')}
          title={t('pages.ContextSwitchPage.header')}
          onButtonClick={() => logout()}
        />
        <div className={classes.description}>
          {t('pages.ContextSwitchPage.description')}
        </div>
        {isLoading || isSelecting ? (
          <Loader isFetching />
        ) : (
          <div className={classes.list}>
            {data?.map?.((context) => (
              <button
                key={context.tenantId}
                className={classes.item}
                type="button"
                disabled={isSelecting}
                onClick={async () => {
                  setIsSelecting(true);
                  try {
                    const response = await UserService.selectContext({
                      tenantId: context.tenantId,
                    });
                    const { userId, userName } = getAuthData();
                    saveAuthData({
                      accessToken: response.token,
                      userName: response.userName || userName,
                      userId: response.userId || userId,
                    });
                    setTenantTypeFromToken();
                    await checkScreeningAvailability();
                    await new Promise((res) => setTimeout(res, 300));
                    if (!isMountedRef.current) return;

                    // Get roles directly from the new token to avoid stale state
                    const tokenData = getTokenData();
                    const rawRoles =
                      tokenData.role ??
                      tokenData.roles ??
                      tokenData.Role ??
                      tokenData.Roles;
                    let rolesList = [];
                    if (Array.isArray(rawRoles)) {
                      rolesList = rawRoles;
                    } else if (typeof rawRoles === 'string') {
                      rolesList = rawRoles.split(/[ ,]+/).filter(Boolean);
                    }

                    const isInterviewer = rolesList.some(
                      (r) => String(r).toLowerCase() === 'interviewer'
                    );
                    const isClient = rolesList.some(
                      (r) => String(r).toLowerCase() === 'client'
                    );
                    if (isInterviewer) {
                      navigate('/tools/interview-dashboard');
                    } else if (isClient) {
                      navigate('/tools/company-dashboard');
                    } else {
                      navigate('/tools/discover');
                    }
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Context manual select failed', err);
                  } finally {
                    if (isMountedRef.current) {
                      setIsSelecting(false);
                    }
                  }
                }}
              >
                <div className={classes.tenant}>{context.tenantName}</div>
                <div className={classes.roles}>
                  {context.roles?.map?.((role) => (
                    <Role key={role.id} role={role.name} />
                  ))}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
